import { notification } from "antd";
import cuid from "cuid";
import { find, get, intersection, isEmpty, map } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAdvancedFilters } from "shared/components/AdvancedFilter";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { useScreenActionPermissions } from "shared/hooks/usePermissions";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import {
  clearProjectRuntime,
  isAcorisProcessTheme,
  persistProcessRuntime,
} from "screens/Private/Broker/Projects/Detail/resolveProcessRuntime";
import graphql from "utils/api/graphql";
import {
  CREATE_PROJECT,
  EXPORT_PROJECTS,
  UPDATE_PROJECT,
  UPDATE_PROJECT_LIGHT,
} from "utils/api/graphql/mutations/projects";
import { PROCESS_STATUSES } from "utils/api/graphql/queries/process-statuses";
import { SUBSCRIPTIONS_LIST } from "utils/api/graphql/queries/subscription";
import { TRACERS } from "utils/api/graphql/queries/tracers";
import {
  ACTION_TYPES,
  CONTENT_TYPES,
  HOT_LIST,
  INITIAL_VALUES,
  KEYS,
  MODULES,
  PROCESS_TAGS,
  PROJECTS_SCOPE,
  SCREENS,
  SUBMODULES,
  SUBSCRIPTION_STATUSES,
  TRACER_TYPES,
  USER_TYPES,
} from "utils/constants";
import { ADVANCED_FILTERS } from "utils/constants/filters";
import { downloadDocument } from "utils/helpers/files";
import {
  clearPaginationHistory,
  getProjectsListPaginationFootprint,
  getProjectsListPaginationHistory,
} from "utils/helpers/pagination";
import { filterByConditions } from "../Detail";
import { buildNewProjectData, resolveFirstProcessStatus } from "../newProject";
import View from "./View";
import { selectableOfferTracers } from "./offerTracers";
import moment from "moment";
import {
  matchStatusesWithProcess as matchStatusesWithProcessHelper,
  resolveProjectStatus,
} from "utils/helpers/projectStatus";

const resolveProjectsListScope = (profile) => {
  const globalViewEnabled =
    get(profile, "organization.config.globalProjectsViewEnabled") === true;
  const isInsuranceAdmin =
    get(profile, "meta.type") === USER_TYPES.INSURANCE_ADMIN.type;

  if (!globalViewEnabled || isInsuranceAdmin) {
    return PROJECTS_SCOPE.MINE;
  }

  const stored = localStorage.getItem(KEYS.PROJECTS_SCOPE_TAB);
  if (
    stored === PROJECTS_SCOPE.MINE ||
    stored === PROJECTS_SCOPE.ORGANIZATION
  ) {
    return stored;
  }

  if (get(profile, "meta.type") === USER_TYPES.INTERNAL_SUPERVISOR.type) {
    return PROJECTS_SCOPE.ORGANIZATION;
  }

  return PROJECTS_SCOPE.MINE;
};

const readPersistedSearch = () => {
  try {
    return JSON.parse(localStorage.getItem(KEYS.PROJECTS_SEARCH) || "{}") || {};
  } catch (_error) {
    return {};
  }
};

const buildProjectSearchLike = ({
  clientQuery = "",
  advisorFirstName = "",
  advisorLastName = "",
} = {}) => {
  const term = clientQuery.trim();
  const compactTerm = term.replace(/\s+/g, "");
  return [
    ...(term
      ? [
          {
            OR: [
              { contact: { user: { firstname: `%${term}%` } } },
              { contact: { user: { lastname: `%${term}%` } } },
              { contact: { telecoms: { value: `%${term}%` } } },
              ...(compactTerm !== term
                ? [{ contact: { telecoms: { value: `%${compactTerm}%` } } }]
                : []),
            ],
          },
        ]
      : []),
    ...(advisorFirstName
      ? [{ author: { firstname: `%${advisorFirstName}%` } }]
      : []),
    ...(advisorLastName
      ? [{ author: { lastname: `%${advisorLastName}%` } }]
      : []),
  ];
};

const isHotListEnabledFor = (profile) =>
  get(profile, "organization.config.hotListAvailable") === true;

const isSignalFeedEnabledFor = (profile) =>
  isHotListEnabledFor(profile) &&
  get(profile, "organization.config.hotList.signalFeed") === true;

const resolveSignalFeedVisible = () =>
  localStorage.getItem(KEYS.PROJECTS_SIGNAL_FEED) !== "false";

const resolveHotMode = (profile) =>
  isHotListEnabledFor(profile) &&
  localStorage.getItem(KEYS.PROJECTS_HOT_MODE) === "true";

const SIGNED_LIKE_PROCESS_TAGS = [
  PROCESS_TAGS.SIGNED,
  PROCESS_TAGS.SIGNED_INCOMPLETE,
  PROCESS_TAGS.SENT_TO_MANAGEMENT,
];

const List = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const [loading, setLoading] = useState(true);
  const [tracer, setTracer] = useState(null);
  const [tracers, setTracers] = useState([]);
  const offerTracers = useMemo(
    () => selectableOfferTracers(tracers),
    [tracers],
  );
  const { user, setUser } = useContext(StoreContext);
  const [profileSynced, setProfileSynced] = useState(false);
  const isGlobalViewEnabled = get(
    user,
    "organization.config.globalProjectsViewEnabled",
    false,
  );
  const isInsuranceAdmin =
    get(user, "meta.type") === USER_TYPES.INSURANCE_ADMIN.type;
  const showScopeToggle = isGlobalViewEnabled && !isInsuranceAdmin;
  const isHotListEnabled = isHotListEnabledFor(user);
  const isSignalFeedEnabled = isSignalFeedEnabledFor(user);
  const [signalFeedVisible, setSignalFeedVisible] = useState(
    resolveSignalFeedVisible,
  );
  const isLeadTypologyEnabled =
    get(user, "organization.config.leadTypologyEnabled") === true;

  const [scope, setScope] = useState(PROJECTS_SCOPE.MINE);
  const [hotMode, setHotMode] = useState(false);
  const [pagination, setPagination] = useState({});

  const [refreshProfile] = useLazyQuery(graphql.queries.CHECK_USER, {
    fetchPolicy: "network-only",
    onError: () => setProfileSynced(true),
    onCompleted: ({ profile }) => {
      if (get(profile, "meta.type")) {
        setUser({ user: profile });
        const nextScope = resolveProjectsListScope(profile);
        const nextHotMode = resolveHotMode(profile);
        setScope(nextScope);
        setHotMode(nextHotMode);
        setPagination(getProjectsListPaginationHistory(nextScope, nextHotMode));
      }
      setProfileSynced(true);
    },
  });

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const paginationFootprint = useMemo(
    () => getProjectsListPaginationFootprint(scope, hotMode),
    [scope, hotMode],
  );
  const [initialSearch] = useState(readPersistedSearch);
  const [filter, setFilter] = useState({
    where: { visible: true },
    like: { AND: buildProjectSearchLike(initialSearch) },
    withSubscriptions: false,
    withContract: false,
    withProcess: false,
    withProcessStatuses: false,
    withTracers: false,
    withFlows: false,
    exclude: [
      "subscriptions",
      "contract",
      "process",
      "processStatuses",
      "flows",
      "tracers",
      "possibleStatuses",
      "conditions",
      "actions",
      "actionsConditions",
    ],
  });
  const [isAssigning, setIsAssigning] = useState({});
  const actions = useScreenActionPermissions({
    module: MODULES.PROJECT,
    submodule: SUBMODULES.BROKER_LIST,
  });

  const advancedFilterHook = useAdvancedFilters({
    filterType: ADVANCED_FILTERS.PROJECT.savedFilterType,
    persistKey: KEYS.PROJECTS_ADVANCED_FILTERS,
  });

  const scopeCountVariables = useMemo(
    () => ({
      where: filter.where,
      like: filter.like,
      ...(advancedFilterHook.advancedFiltersVariable
        ? { advancedFilters: advancedFilterHook.advancedFiltersVariable }
        : {}),
    }),
    [filter.where, filter.like, advancedFilterHook.advancedFiltersVariable],
  );

  const { data: mineScopeCountData } = useQuery(
    graphql.queries.COUNT_PROJECTS,
    {
      skip: !showScopeToggle || loading,
      fetchPolicy: "network-only",
      variables: {
        ...scopeCountVariables,
        scope: PROJECTS_SCOPE.MINE,
      },
    },
  );

  const { data: organizationScopeCountData } = useQuery(
    graphql.queries.COUNT_PROJECTS,
    {
      skip: !showScopeToggle || loading,
      fetchPolicy: "network-only",
      variables: {
        ...scopeCountVariables,
        scope: PROJECTS_SCOPE.ORGANIZATION,
      },
    },
  );

  const scopeCounts = {
    mine: get(mineScopeCountData, "countProjects"),
    organization: get(organizationScopeCountData, "countProjects"),
  };

  const [getProcessStatuses, { data: processStatuses }] =
    useLazyQuery(PROCESS_STATUSES);

  useQuery(TRACERS, {
    fetchPolicy: "no-cache",
    variables: { where: { type: TRACER_TYPES.MANUAL, isExternal: false } },
    onCompleted: ({ tracers: { data } }) => {
      setTracers(omitDeep(data, "__typename"));
      setLoading(false);
    },
  });

  const matchStatusesWithProcess = (statuses) =>
    matchStatusesWithProcessHelper(statuses, tracers);

  const [updateProject] = useMutation(UPDATE_PROJECT, {});
  const [lightUpdateProject] = useMutation(UPDATE_PROJECT_LIGHT, {
    update: (cache, { data: { updateProject: updated } }) => {
      const cacheId = cache.identify({
        __typename: "Project",
        id: updated.id,
      });
      cache.modify({
        id: cacheId,
        fields: {
          author: () => updated.author,
        },
      });
    },
  });

  const [duplicateProject] = useMutation(graphql.mutations.DUPLICATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECTS_LIST,
        awaitRefetchQueries: true,
        variables: {
          where: {
            visible: true,
          },
          ...INITIAL_VALUES,
          ...filter,
        },
      },
    ],
    onCompleted: ({ duplicateProject }) => {
      clearPaginationHistory("projects");
      const process = get(tracer, "flow.process");
      const firstStatus = resolveFirstProcessStatus(process);
      clearProjectRuntime();
      persistProcessRuntime(
        duplicateProject.id,
        process,
        tracer,
        get(firstStatus, "id"),
      );
      if (get(firstStatus, "id")) {
        localStorage.setItem(KEYS.STATUS_ID, firstStatus.id);
      }
      navigate(
        `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${duplicateProject.id}`,
        {
          state: {
            process,
            tracer,
            status: firstStatus,
          },
        },
      );
    },
  });

  const [loadSubscriptions] = useLazyQuery(SUBSCRIPTIONS_LIST);

  const [exportProjects, { loading: exportLoading }] =
    useMutation(EXPORT_PROJECTS);
  const [sendProjectsByEmail, { loading: sendByEmailLoading }] = useMutation(
    graphql.mutations.SEND_PROJECTS_BY_EMAIL,
  );
  const [showExportModal, setShowExportModal] = useState(false);

  const handleExportProjects = async (variables = { where: {} }) => {
    try {
      const { data } = await exportProjects({
        variables: {
          ...variables,
          where: {
            ...(variables.where || {}),
            visible: true,
          },
        },
      });
      if (data?.exportProjects) {
        downloadDocument({
          base64: data.exportProjects,
          contentType: CONTENT_TYPES.XLSX,
          name: "projects.xlsx",
        });
      }
    } catch (error) {
      console.error("Error exporting projects:", error);
    }
  };

  const handleSendProjectsByEmail = async (variables = { where: {} }) => {
    try {
      // Afficher une notification indiquant que l'email sera envoyé
      notification.info({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_TITLE"),
        description: t("LIST.HEADER.EXPORT_MODAL.EMAIL_WILL_BE_SENT_MESSAGE", {
          email: user?.email || "",
        }),
        duration: 10,
      });

      const { data } = await sendProjectsByEmail({
        variables: {
          ...variables,
          where: {
            ...(variables.where || {}),
            visible: true,
          },
        },
      });
      if (data?.sendProjectsByEmail !== undefined) {
        // Success - email sent
        notification.success({
          message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_TITLE"),
          description: t(
            "LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_SUCCESS_MESSAGE",
            {
              count: data.sendProjectsByEmail,
            },
          ),
          duration: 10,
        });
      }
    } catch (error) {
      console.error("Error sending projects by email:", error);
      notification.error({
        message: t("LIST.HEADER.EXPORT_MODAL.EMAIL_SENT_ERROR_TITLE"),
        description: error.message,
        duration: 10,
      });
    }
  };

  const projectDetailTarget = (project) => {
    const { tracer: projectTracer, processStatus } = resolveProjectStatus(
      project,
      tracers,
    );
    const processTheme = get(projectTracer, "flow.process.theme");
    return {
      path: `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${project.id}`,
      state: {
        process: get(projectTracer, "flow.process"),
        tracer: projectTracer,
        ...(isAcorisProcessTheme(processTheme)
          ? { status: processStatus, resumeFromList: true }
          : {}),
      },
    };
  };

  const resolveProjectTarget = async (project) => {
    const processTags = get(project, "statuses", [])
      .map((row) => get(row, "status.tag"))
      .filter(Boolean);
    const isSignedLike =
      intersection(SIGNED_LIKE_PROCESS_TAGS, processTags).length > 0;
    const fieldSubscriptionId = get(project, "fields.currentSubscription");

    if (isSignedLike) {
      if (fieldSubscriptionId) {
        return {
          path: `${SCREENS.PRIVATE.BROKER.SUBSCRIPTIONS.DETAIL.path}/${fieldSubscriptionId}`,
        };
      }
      try {
        const { data } = await loadSubscriptions({
          variables: {
            where: { project: { id: project.id } },
            exclude: ["telecoms"],
          },
        });
        const rows = get(data, "subscriptionsList.data", []) || [];
        const signed =
          rows.find(
            (row) => get(row, "status") === SUBSCRIPTION_STATUSES.SIGNED,
          ) || rows[rows.length - 1];
        if (get(signed, "id")) {
          return {
            path: `${SCREENS.PRIVATE.BROKER.SUBSCRIPTIONS.DETAIL.path}/${signed.id}`,
          };
        }
      } catch (_error) {
        // Fall through to the project devis screen.
      }
    }

    return projectDetailTarget(project);
  };

  const onDetail = async (project) => {
    const { path, state } = await resolveProjectTarget(project);
    navigate(path, state ? { state } : undefined);
  };

  const onOpenInTab = (project) => {
    const { path, state } = projectDetailTarget(project);
    if (get(state, "process.id")) {
      persistProcessRuntime(project.id, state.process, state.tracer);
    }
    window.open(
      `${window.location.origin}${window.location.pathname}#${path}`,
      "_blank",
    );
  };

  const onDelete = (id) =>
    navigate(`${SCREENS.PRIVATE.BROKER.PROJECTS.DELETE.path}/${id}`);

  const onChangeScope = (nextScope) => {
    if (nextScope === scope) return;
    setScope(nextScope);
    localStorage.setItem(KEYS.PROJECTS_SCOPE_TAB, nextScope);
    setPagination(getProjectsListPaginationHistory(nextScope, hotMode));
  };

  const onToggleHotMode = () => {
    const nextHotMode = !hotMode;
    setHotMode(nextHotMode);
    localStorage.setItem(KEYS.PROJECTS_HOT_MODE, String(nextHotMode));
    setPagination(getProjectsListPaginationHistory(scope, nextHotMode));
  };

  const onToggleSignalFeed = () => {
    const nextVisible = !signalFeedVisible;
    setSignalFeedVisible(nextVisible);
    localStorage.setItem(KEYS.PROJECTS_SIGNAL_FEED, String(nextVisible));
  };

  const isHotModeActive = isHotListEnabled && hotMode;
  const showSignalFeed =
    isHotModeActive && isSignalFeedEnabled && signalFeedVisible;

  const onSearch = (search) => {
    try {
      localStorage.setItem(KEYS.PROJECTS_SEARCH, JSON.stringify(search || {}));
    } catch (_error) {}
    setPagination((prev) => ({ ...prev, skip: 0 }));
    setFilter((prev) => ({
      ...prev,
      like: { ...prev.like, AND: buildProjectSearchLike(search) },
    }));
  };

  const applyFilter = ({ expired, tags = [], offers = [] }) => {
    const tagFilter = [
      ...(expired
        ? [
            {
              AND: [
                { expired },
                {
                  OR: [
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.CURRENT_SUBSCRIPTION },
                      },
                    },
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.IN_PROGRESS },
                      },
                    },
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.QUOTE_SENT },
                      },
                    },
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.SIGNED },
                      },
                    },
                    {
                      statuses: {
                        status: { tag: PROCESS_TAGS.SIGNED_INCOMPLETE },
                      },
                    },
                    {
                      statuses: {
                        status: {
                          tag: PROCESS_TAGS.ELECTRONIC_SIGNATURE_PENDING,
                        },
                      },
                    },
                  ],
                },
              ],
            },
          ]
        : []),
      ...tags?.map((tag) => ({
        AND: [
          { statuses: { status: { tag } } },
          ...(tag === PROCESS_TAGS.CLOSE ? [] : [{ expired: false }]),
        ],
      })),
    ];
    setPagination((prev) => ({ ...prev, skip: 0 }));
    setFilter((prev) => ({
      ...(isEmpty(offers) && !offers.length
        ? {
            ...prev,
            withSubscriptions: false,
            withContract: false,
            withProcess: false,
            withProcessStatuses: false,
            withTracers: false,
            withFlows: false,
            exclude: [
              "subscriptions",
              "contract",
              "process",
              "processStatuses",
              "flows",
              "tracers",
              "possibleStatuses",
              "conditions",
              "actions",
              "actionsConditions",
            ],
          }
        : {
            ...prev,
            withSubscriptions: false,
            withContract: false,
            withProcess: true,
            withTracers: true,
            withFlows: true,
            withProcessStatuses: false,
            exclude: [
              "subscriptions",
              "contract",
              // "process",
              "processStatuses",
              // "flows",
              // "tracers",
              "possibleStatuses",
              "conditions",
              "actions",
              "actionsConditions",
            ],
          }),
      where: {
        ...prev.where,
        AND: [
          ...(!isEmpty(tagFilter)
            ? [
                {
                  OR: tagFilter,
                },
              ]
            : []),
          ...(!isEmpty(offers)
            ? [
                {
                  OR: offers?.map((offer) => ({
                    statuses: {
                      status: {
                        process: {
                          flows: {
                            tracers: {
                              id: get(
                                find(tracers, ({ name }) => name === offer),
                                "id",
                              ),
                            },
                          },
                        },
                      },
                    },
                  })),
                },
              ]
            : []),
        ],
      },
    }));
  };

  const [addProject] = useMutation(CREATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECTS_LIST,
        awaitRefetchQueries: true,
        variables: { where: { visible: true }, ...filter, ...INITIAL_VALUES },
      },
    ],
    onCompleted: ({ addProject }) => {
      clearPaginationHistory("projects");
      const process = get(tracer, "flow.process");
      const firstStatus = resolveFirstProcessStatus(process);
      persistProcessRuntime(
        addProject.id,
        process,
        tracer,
        get(firstStatus, "id"),
      );
      navigate(
        `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${addProject.id}`,
        {
          state: {
            process,
            tracer,
            status: firstStatus,
          },
        },
      );
    },
  });

  const createProject = (tracer) => {
    setTracer(tracer);
    clearProjectRuntime();
    addProject({
      variables: { data: buildNewProjectData({ tracer, userId: user.id }) },
    });
  };

  const onAssign = ({ projectId, projectFields = {}, authorId }) => {
    setIsAssigning({ [projectId]: true });
    return lightUpdateProject({
      variables: {
        where: { id: projectId },
        data: {
          author: { id: authorId },
          fields: { ...projectFields, isManualAssign: true },
        },
      },
      onCompleted: () => setTimeout(() => setIsAssigning({}), 1000),
    });
  };

  const onChangeStatus = ({ project: projectData, status }) => {
    const project = omitDeep(projectData, "__typename");
    const actionsToExecute = filterByConditions(status?.actions || [], project);
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({ id: cuid(), status: { id: status } }));
    const noSyncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.NO_SYNC_PROCESSES)
      .map(({ args: { process } }) =>
        get(project, "statuses", []).find(
          ({ status }) => get(status, "process.id") === process,
        ),
      )
      .filter(Boolean);
    const statuses = [
      {
        id: cuid(),
        status: { id: status.id },
      },
      ...syncActions,
      ...noSyncActions,
    ];

    updateProject({
      variables: {
        where: { id: project?.id },
        data: { statuses },
      },
      onCompleted: () => setTimeout(() => setIsAssigning({}), 1000),
    });
  };

  if (loading || !profileSynced) return <Loading />;

  const filterWithAdvanced = {
    ...filter,
    ...(showScopeToggle ? { scope } : {}),
    ...(isHotModeActive ? { orderBy: HOT_LIST.ORDER_BY } : {}),
    ...(advancedFilterHook.advancedFiltersVariable
      ? { advancedFilters: advancedFilterHook.advancedFiltersVariable }
      : {}),
  };

  return (
    <View
      filter={filterWithAdvanced}
      matchStatusesWithProcess={matchStatusesWithProcess}
      onDetail={onDetail}
      onOpenInTab={onOpenInTab}
      pagination={pagination}
      setPagination={setPagination}
      onSearch={onSearch}
      initialSearch={initialSearch}
      applyFilter={applyFilter}
      duplicate={duplicateProject}
      createProject={createProject}
      onDelete={onDelete}
      actions={actions}
      exportProjects={() => setShowExportModal(true)}
      exportLoading={exportLoading}
      sendByEmailLoading={sendByEmailLoading}
      showExportModal={showExportModal}
      setShowExportModal={setShowExportModal}
      handleExportProjects={handleExportProjects}
      handleSendProjectsByEmail={handleSendProjectsByEmail}
      onAssign={onAssign}
      isAssigning={isAssigning}
      processStatuses={get(processStatuses, "processStatuses.data", [])}
      onChangeStatus={onChangeStatus}
      tracers={tracers}
      offerTracers={offerTracers}
      setTracers={setTracers}
      setTracer={setTracer}
      user={user}
      scope={scope}
      showScopeToggle={showScopeToggle}
      scopeCounts={scopeCounts}
      onChangeScope={onChangeScope}
      isHotListEnabled={isHotListEnabled}
      hotMode={isHotModeActive}
      onToggleHotMode={onToggleHotMode}
      isSignalFeedEnabled={isHotModeActive && isSignalFeedEnabled}
      showSignalFeed={showSignalFeed}
      onToggleSignalFeed={onToggleSignalFeed}
      paginationFootprint={paginationFootprint}
      advancedFilterProps={advancedFilterHook}
      isLeadTypologyEnabled={isLeadTypologyEnabled}
    />
  );
};

export default List;
